import { useCallback, useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import { Chip } from '@jotpsych/ui';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import type { DocRefSection } from '@store/generated/document-references';

import { useArtifactDownload, useDocumentReference } from '../../store/hooks';
import type { DocumentReferenceMetadata } from '../../types';

import { kindLabel, kindStyle } from './constants';
import { formatDateRange } from './helpers';

interface DocRefDrawerProps {
  documentReferenceId: string | null;
  initialSectionId?: string | null;
  onClose: () => void;
}

/**
 * Right-side drawer showing a single DocumentReference's detail view:
 *
 * - Left: PDF stand-in with current page indicator (full PDF viewer
 *   deferred; this proves out the section-jump interaction).
 * - Right: AI description + clickable section list + tags + processing
 *   stages + file metadata.
 *
 * Fetches via `useDocumentReference` so the heavy enrichment payload
 * (sections + bboxes + per-section summaries) loads on demand rather
 * than shipping with every list row.
 */
export function DocRefDrawer({
  documentReferenceId,
  initialSectionId,
  onClose,
}: DocRefDrawerProps) {
  const { documentReference, isLoading } = useDocumentReference(documentReferenceId);
  const fetchDownloadUrl = useArtifactDownload();
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);

  useEffect(() => {
    if (!documentReference) {
      setActiveSectionId(initialSectionId ?? null);
      return;
    }
    const sections = summarySections(documentReference.metadata);
    setActiveSectionId(initialSectionId ?? sections[0]?.section_id ?? null);
  }, [documentReferenceId, documentReference, initialSectionId]);

  // Download routes through the source artifact (DocRefs don't have
  // their own files — they reference the raw upload's bytes).
  const handleDownload = useCallback(async () => {
    const sourceArtifactId = documentReference?.source_artifact_id;
    if (!sourceArtifactId) return;
    try {
      const url = await fetchDownloadUrl(sourceArtifactId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      // Failures surface via the lazy query's own error state.
    }
  }, [documentReference?.source_artifact_id, fetchDownloadUrl]);

  const open = documentReferenceId != null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose} slotProps={{ paper: {
        sx: {
          width: { xs: '100%', md: 880 },
          maxWidth: '100%',
        },
      } }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
            {documentReference?.title ||
              (isLoading ? 'Loading…' : 'Document')}
          </Typography>
          {documentReference && (
            <Typography variant="caption" color="text.secondary">
              {[
                documentReference.author_facility,
                formatDateRange(
                  documentReference.authored_at,
                  documentReference.signed_at,
                ),
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          )}
        </Box>
        {documentReference?.source_artifact_id && (
          <Button
            size="small"
            startIcon={<DownloadIcon fontSize="small" />}
            onClick={handleDownload}
            sx={{ textTransform: 'none' }}
          >
            Open original
          </Button>
        )}
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 320px' },
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {/* PDF stand-in */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'grey.50',
            borderRight: { md: '1px solid' },
            borderColor: { md: 'divider' },
            overflow: 'auto',
          }}
        >
          {isLoading || !documentReference ? (
            <Stack sx={{alignItems: "center", justifyContent: "center",  flex: 1, py: 8 }}>
              <CircularProgress size={20} />
            </Stack>
          ) : (
            <PdfStandIn
              title={documentReference.title}
              authorFacility={documentReference.author_facility}
              authorProviderName={documentReference.author_provider_name}
              description={documentReference.description}
              activeSection={
                summarySections(documentReference.metadata).find(
                  s => s.section_id === activeSectionId,
                ) ?? null
              }
            />
          )}
        </Box>

        <Box
          sx={{
            p: 2,
            overflow: 'auto',
            bgcolor: 'background.paper',
          }}
        >
          {documentReference && (
            <IntelPanel
              metadata={documentReference.metadata}
              docKind={documentReference.doc_kind}
              tags={documentReference.tags}
              activeSectionId={activeSectionId}
              onSelectSection={setActiveSectionId}
            />
          )}
        </Box>
      </Box>
    </Drawer>
  );
}

function PdfStandIn({
  title,
  authorFacility,
  authorProviderName,
  description,
  activeSection,
}: {
  title?: string | null;
  authorFacility?: string | null;
  authorProviderName?: string | null;
  description?: string | null;
  activeSection: DocRefSection | null;
}) {
  const pageNumber = activeSection?.page_start ?? 1;
  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          bgcolor: 'background.paper',
          p: 4,
          borderRadius: 1,
          boxShadow: 1,
          minHeight: 520,
        }}
      >
        <Typography variant="h6" sx={{ mb: 1 }}>
          {authorFacility || title || 'Document'}
        </Typography>
        {authorProviderName && (
          <Typography variant="caption" color="text.secondary">
            {authorProviderName}
          </Typography>
        )}
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" color="text.secondary">
          {activeSection
            ? `Section: ${activeSection.title ?? ''}`
            : 'PDF preview (coming soon)'}
        </Typography>
        {description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2, whiteSpace: 'pre-wrap' }}
          >
            {description}
          </Typography>
        )}
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ mt: 4, display: 'block', textAlign: 'center' }}
        >
          p. {pageNumber}
        </Typography>
      </Box>
    </Box>
  );
}

function IntelPanel({
  metadata,
  docKind,
  tags,
  activeSectionId,
  onSelectSection,
}: {
  metadata: DocumentReferenceMetadata | undefined;
  docKind?: string | null;
  tags?: string[];
  activeSectionId: string | null;
  onSelectSection: (id: string) => void;
}) {
  const sections = summarySections(metadata);
  const stages = metadata?.processing?.stages_completed ?? [];
  const color = kindStyle(docKind);

  return (
    <Stack spacing={2.5}>
      {docKind && (
        <Box>
          <SectionLabel>Kind</SectionLabel>
          <Chip
            size="small"
            label={kindLabel(docKind)}
            sx={{
              backgroundColor: color.bg,
              color: color.fg,
              fontWeight: 600,
              border: 0,
            }}
          />
        </Box>
      )}

      {sections.length > 0 && (
        <Box>
          <SectionLabel>Sections</SectionLabel>
          <Stack spacing={0}>
            {sections.map(sec => {
              const selected = sec.section_id === activeSectionId;
              return (
                <Box
                  key={sec.section_id ?? sec.title ?? Math.random()}
                  role="button"
                  tabIndex={0}
                  onClick={() => sec.section_id && onSelectSection(sec.section_id)}
                  onKeyDown={e => {
                    if ((e.key === 'Enter' || e.key === ' ') && sec.section_id) {
                      e.preventDefault();
                      onSelectSection(sec.section_id);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 1,
                    p: 1,
                    borderRadius: 0.5,
                    cursor: 'pointer',
                    bgcolor: selected ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, display: 'block' }}
                    >
                      {sec.title}
                    </Typography>
                    {sec.summary && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.25 }}
                      >
                        {sec.summary}
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ flexShrink: 0 }}
                  >
                    p{sec.page_start ?? '?'}
                    {sec.page_end != null && sec.page_end !== sec.page_start
                      ? `–${sec.page_end}`
                      : ''}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}

      {tags && tags.length > 0 && (
        <Box>
          <SectionLabel>Tags</SectionLabel>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {tags.map(tag => (
              <Chip
                key={tag}
                size="small"
                label={tag}
                variant="outlined"
                sx={{ fontSize: 11 }}
              />
            ))}
          </Box>
        </Box>
      )}

      {stages.length > 0 && (
        <Box>
          <SectionLabel>Processing</SectionLabel>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {stages.map(st => (
              <Chip key={st} size="small" label={st} variant="soft" color="success" />
            ))}
          </Box>
        </Box>
      )}

      {metadata?.processing?.error && (
        <Alert severity="error" sx={{ py: 0.5 }}>
          <Typography variant="caption">{metadata.processing.error}</Typography>
        </Alert>
      )}
    </Stack>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="overline"
      color="text.secondary"
      sx={{ display: 'block', mb: 0.75, fontSize: 10 }}
    >
      {children}
    </Typography>
  );
}

function summarySections(
  metadata: DocumentReferenceMetadata | undefined,
): DocRefSection[] {
  return metadata?.sections ?? [];
}
